// ============================================================================
// Componente: AutomacaoWellhub.js
// Objetivo: Interface para automação da gestão do Wellhub (De-Para).
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import * as XLSX from 'xlsx';
import { 
    ArrowLeft, UploadCloud, FileSpreadsheet, Play, 
    AlertCircle, Loader2, Users, HelpCircle, Download,
    ChevronLeft, ChevronRight, CheckCircle
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';

const AutomacaoWellhub = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    // Estados da Automação
    const [arquivoBase, setArquivoBase] = useState(null);
    const [emailsWellhub, setEmailsWellhub] = useState([]);
    const [selectedType, setSelectedType] = useState('adicionar');
    const [reportData, setReportData] = useState([]);
    
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Paginação da Tabela
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';

    // Estilos Auxiliares
    const corModulo = '#e11d48'; 

    useEffect(() => {
        const handleResize = () => { 
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    // ==========================================
    // FUNÇÕES DE ARQUIVO (DRAG AND DROP)
    // ==========================================
    const processarArquivo = (file) => {
        if (!file) return;
        
        setArquivoBase(file);
        setReportData([]); 
        setErrorMessage('');
        
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);
                
                const emails = data.map(row => row['Email']).filter(email => email !== undefined && email.includes('@'));
                if (emails.length === 0) {
                    setErrorMessage('Nenhum e-mail encontrado. Certifique-se de que a planilha contém a coluna "Email".');
                } else {
                    setEmailsWellhub(emails);
                }
            } catch (error) {
                setErrorMessage('Erro ao ler a planilha. Verifique se o formato está correto.');
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            processarArquivo(e.target.files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = corModulo;
        e.currentTarget.style.backgroundColor = '#fff1f2';
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = '#cbd5e1';
        e.currentTarget.style.backgroundColor = '#f8fafc';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.currentTarget.style.borderColor = '#cbd5e1';
        e.currentTarget.style.backgroundColor = '#f8fafc';
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processarArquivo(e.dataTransfer.files[0]);
        }
    };

    // ==========================================
    // INTEGRAÇÃO COM BACK-END
    // ==========================================
    const gerarRelatorio = async () => {
        if (emailsWellhub.length === 0) {
            setErrorMessage('A planilha base não contém e-mails válidos para o cruzamento.');
            return;
        }

        setIsProcessing(true);
        setErrorMessage('');
        
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`/api/automacao-rh/rh-wellhub/${selectedType}`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ emailsWellhub })
            });
            
            if (!response.ok) throw new Error("Erro na requisição ao servidor.");
            
            const data = await response.json();
            setReportData(data);
            setCurrentPage(1); 
        } catch (error) {
            console.error("Erro ao cruzar dados:", error);
            setErrorMessage('Erro ao cruzar os dados. Verifique a base e tente novamente.');
        } finally {
            setIsProcessing(false);
        }
    };

    const downloadExcel = () => {
        if (reportData.length === 0) return;
        const worksheet = XLSX.utils.json_to_sheet(reportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Wellhub");
        XLSX.writeFile(workbook, `Wellhub_${selectedType}.xlsx`); 
    };

    // ==========================================
    // PAGINAÇÃO
    // ==========================================
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = reportData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(reportData.length / itemsPerPage);

    const irParaProximaPagina = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };
    const irParaPaginaAnterior = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%', overflowX: 'hidden' }}>

                {/* CABEÇALHO PREMIUM */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> RH
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Users size={24} color={corModulo} /> Wellhub (De-Para)
                            </h2>
                            <button
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#fff1f2', color: corModulo, border: '1px solid #fecdd3', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>

                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: isMobile ? '20px' : '40px' }} className="max-w-6xl mx-auto">
                    
                    {/* CARTÃO PRINCIPAL DE CONFIGURAÇÃO */}
                    <div style={{ backgroundColor: '#fff', padding: isMobile ? '20px' : '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '30px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Configuração da Automação</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>Faça o upload da base de ativos do Wellhub e selecione o tipo de relatório para realizar o cruzamento de dados com o sistema.</p>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '20px', marginBottom: '24px' }}>
                            
                            {/* ÁREA DRAG AND DROP */}
                            <div 
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                style={{ 
                                    border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '30px 20px', 
                                    textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer',
                                    transition: 'all 0.2s ease', position: 'relative'
                                }}
                            >
                                <input 
                                    type="file" 
                                    accept=".xlsx, .xls, .csv" 
                                    onChange={handleFileChange}
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                    disabled={isProcessing}
                                />
                                
                                {!arquivoBase ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ backgroundColor: '#ffe4e6', padding: '12px', borderRadius: '50%', color: corModulo }}>
                                            <UploadCloud size={28} />
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#334155' }}>Arraste o arquivo aqui ou clique para buscar</p>
                                            <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Apenas arquivos Excel ou CSV</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ backgroundColor: '#ecfdf5', padding: '12px', borderRadius: '50%', color: '#10b981' }}>
                                            <FileSpreadsheet size={28} />
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#0f172a' }}>{arquivoBase.name}</p>
                                            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                                                {(arquivoBase.size / 1024 / 1024).toFixed(2)} MB • {emailsWellhub.length} registros válidos
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* SELECT DE TIPO */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                                    Tipo de Cruzamento
                                </label>
                                <select 
                                    style={{
                                        height: '48px', padding: '0 16px', borderRadius: '10px', border: '1px solid #cbd5e1',
                                        backgroundColor: '#f8fafc', color: '#1e293b', fontSize: '14px', outline: 'none',
                                        cursor: 'pointer', transition: 'border-color 0.2s'
                                    }}
                                    value={selectedType}
                                    onChange={(e) => {
                                        setSelectedType(e.target.value);
                                        setReportData([]); 
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = corModulo}
                                    onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                                >
                                    <option value="adicionar">Adicionar Colaboradores (Novos)</option>
                                    <option value="remover">Remover Colaboradores (Desligados)</option>
                                </select>
                            </div>
                        </div>

                        {errorMessage && (
                            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#ef4444', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '14px', fontWeight: '500' }}>
                                <AlertCircle size={18} /> {errorMessage}
                            </div>
                        )}

                        {/* BOTÃO EXECUTAR */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                            <button 
                                onClick={gerarRelatorio}
                                disabled={isProcessing || emailsWellhub.length === 0}
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', 
                                    backgroundColor: isProcessing || emailsWellhub.length === 0 ? '#cbd5e1' : corModulo, 
                                    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', 
                                    fontSize: '15px', cursor: isProcessing || emailsWellhub.length === 0 ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s', boxShadow: isProcessing || emailsWellhub.length === 0 ? 'none' : '0 4px 6px -1px rgba(225, 29, 72, 0.2)'
                                }}
                            >
                                {isProcessing ? (
                                    <><Loader2 size={18} className="animate-spin" /> Processando...</>
                                ) : (
                                    <><Play size={18} fill="currentColor" /> Cruzar Dados</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* CARD DA TABELA (PREVIEW) */}
                    {!isProcessing && reportData.length > 0 && (
                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            
                            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Visualização dos Dados</h3>
                                    <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>Foram encontrados {reportData.length} registros de divergência.</p>
                                </div>
                                
                                <button 
                                    onClick={downloadExcel}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '40px', padding: '0 20px', borderRadius: '8px', backgroundColor: '#1e293b', color: '#FFFFFF', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'background-color 0.2s' }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0f172a'}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1e293b'}
                                >
                                    <Download size={16} /> Exportar XLSX
                                </button>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <tr>
                                            {Object.keys(currentItems[0]).map((key) => (
                                                <th key={key} style={{ padding: '14px 24px', textAlign: 'left', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '12px' }}>
                                                    {key}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((row, index) => (
                                            <tr key={index} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                                {Object.values(row).map((val, i) => (
                                                    <td key={i} style={{ padding: '14px 24px', color: '#334155' }}>
                                                        {val}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* PAGINAÇÃO */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#fff' }}>
                                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                                    Mostrando <span style={{ fontWeight: 700, color: '#1e293b' }}>{indexOfFirstItem + 1}</span> a <span style={{ fontWeight: 700, color: '#1e293b' }}>{Math.min(indexOfLastItem, reportData.length)}</span> de <span style={{ fontWeight: 700, color: '#1e293b' }}>{reportData.length}</span> registros
                                </span>
                                
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={irParaPaginaAnterior} disabled={currentPage === 1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: currentPage === 1 ? '#cbd5e1' : '#475569', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                                        <ChevronLeft size={18} />
                                    </button>
                                    <div style={{ display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                        Página {currentPage} de {totalPages}
                                    </div>
                                    <button onClick={irParaProximaPagina} disabled={currentPage === totalPages} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: currentPage === totalPages ? '#cbd5e1' : '#475569', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                                        <ChevronRight size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ESTADO VAZIO APÓS CRUZAMENTO */}
                    {!isProcessing && reportData.length === 0 && emailsWellhub.length > 0 && arquivoBase && (
                        <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', padding: '40px 20px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                                <div style={{ backgroundColor: '#dcfce7', padding: '16px', borderRadius: '50%', color: '#16a34a' }}>
                                    <CheckCircle size={32} />
                                </div>
                            </div>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '800', color: '#166534' }}>Tudo sincronizado!</h3>
                            <p style={{ margin: 0, color: '#15803d', fontSize: '15px' }}>Não foram encontradas divergências para o tipo selecionado ({selectedType}).</p>
                        </div>
                    )}

                </div>
            </div>
            
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="rh-wellhub" 
                showOnlyIds={['rh-wellhub']} 
            />
        </div>
    );
};

export default AutomacaoWellhub;